import { RouteObject, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import ProtectedRoute from "./ProtectedRoute";
import PermissionRoute from "./PermissionRoute";
import OwnerRoute from "./OwnerRoute";
import RemoteLoginPage from "@/features/auth/pages/RemoteLoginPage";
import LegacyRedirect, { FlatToWhatsAppRedirect, FlatToAutomationsRedirect } from "./LegacyRedirect";
import WhatsAppTemplateBuilder from "@/features/whats-app-crm/pages/TemplateBuilder";
import ViewTemplatePage from "@/features/whats-app-crm/components/ViewTemplatePage";
import WhatsAppLinkGenerator from '@/features/free-tools/pages/WhatsAppLinkGenerator';
import WhatsAppQRGenerator from '@/features/free-tools/pages/WhatsAppQRGenerator';
import WhatsAppWidgetGenerator from '@/features/free-tools/pages/WhatsAppWidgetGenerator';

const TemplateLibrary = lazy(() =>
  import("@/features/whats-app-crm/pages/TemplateLibrary")
);

const LoginPage = lazy(() =>
  import("@/features/auth/pages/LoginPage")
);

const RegisterPage = lazy(() =>
  import("@/features/auth/pages/RegisterPage")
);

const OnboardingPage = lazy(() =>
  import("@/features/auth/pages/OnboardingPage")
);

const VerifyOtpPage = lazy(() =>
  import("@/features/auth/pages/VerifyOtpPage")
);

const ForgotPasswordPage = lazy(() =>
  import("@/features/auth/pages/ForgotPasswordPage")
);

const ResetPasswordPage = lazy(() =>
  import("@/features/auth/pages/ResetPasswordPage")
);


const NotFound = lazy(() =>
  import("@/features/error/pages/NotFound")
);

const Plans = lazy(() =>
  import("@/features/plan/pages/Plans")
);

const Setting = lazy(() =>
  import("@/features/setting/pages/Setting")
);

const AffiliateDashboard = lazy(() =>
  import("@/features/affiliate/pages/AffiliateDashboard")
);

const AdminSettingsPage = lazy(() =>
  import("@/features/setting/pages/AdminSettingsPage")
);

const InstagramConnectionPage = lazy(() =>
  import("@/features/instagram-automation/pages/InstagramConnectionPage")
);

const InstagramAutomationListPage = lazy(() =>
  import("@/features/instagram-automation/pages/InstagramAutomationListPage")
);

const InstagramAutomationBuilderPage = lazy(() =>
  import("@/features/instagram-automation/pages/InstagramAutomationBuilderPage")
);

const InstagramAutomationDetailPage = lazy(() =>
  import("@/features/instagram-automation/pages/InstagramAutomationDetailPage")
);

const Appointment = lazy(() =>
  import("@/features/appointment/pages/Appointment")
);

// AI Chatbot v1 Pages
const KnowledgeSources = lazy(() =>
  import("@/features/ai-chatbot-v1/pages/KnowledgeSources")
);

const KnowledgeSourceDetail = lazy(() =>
  import("@/features/ai-chatbot-v1/pages/KnowledgeSourceDetail")
);

const AiChatbotV1ApiTools = lazy(() =>
  import("@/features/ai-chatbot-v1/pages/ApiTools")
);

const AiChatbotV1ApiToolEditor = lazy(() =>
  import("@/features/ai-chatbot-v1/pages/ApiToolEditor")
);

const AiChatbotV1Settings = lazy(() =>
  import("@/features/ai-chatbot-v1/pages/Settings")
);

const AiChatbotV1Leads = lazy(() =>
  import("@/features/ai-chatbot-v1/pages/Leads")
);

const AiChatbotV1TestChat = lazy(() =>
  import("@/features/ai-chatbot-v1/pages/TestChat")
);

const AiChatbotV1Usage = lazy(() =>
  import("@/features/ai-chatbot-v1/pages/Usage")
);

const AiChatbotV1UserDashboard = lazy(() =>
  import("@/features/ai-chatbot-v1/pages/UserDashboard")
);

const AiChatbotV1UsageLogs = lazy(() =>
  import("@/features/ai-chatbot-v1/pages/UsageLogs")
);

const AiChatbotV1UsageSourceDetail = lazy(() =>
  import("@/features/ai-chatbot-v1/pages/UsageSourceDetail")
);

const AiChatbotV1UsageSourcePageDetail = lazy(() =>
  import("@/features/ai-chatbot-v1/pages/UsageSourcePageDetail")
);

const AiChatbotV1UsageConversationDetail = lazy(() =>
  import("@/features/ai-chatbot-v1/pages/UsageConversationDetail")
);

const AiChatbotV1UsageMessageEvents = lazy(() =>
  import("@/features/ai-chatbot-v1/pages/UsageMessageEvents")
);

const TeamManagement = lazy(() =>
  import("@/features/team/pages/TeamManagement")
);
const RolesAndPermissions = lazy(() =>
  import("@/features/team/pages/RolesAndPermissions")
);
const CreateRolePage = lazy(() =>
  import("@/features/team/pages/CreateRolePage")
);
const EditRolePage = lazy(() =>
  import("@/features/team/pages/EditRolePage")
);
const MultiAgentAccessGate = lazy(() =>
  import("@/features/team/components/MultiAgentAccessGate")
);

const Analytics = lazy(() =>
  import("@/features/analytics/pages/Analytics")
);

const AttributesReportsPage = lazy(() =>
  import("@/features/analytics/pages/AttributesReportsPage")
);

const FlowReports = lazy(() =>
  import("@/features/analytics/pages/FlowReports")
);




const ChatbotBuilderLayout = lazy(() =>
  import("@/features/chatbots/components/ChatbotBuilderLayout")
);

const BasicInfo = lazy(() =>
  import("@/features/chatbots/pages/BasicInfo")
);



const DataCollection = lazy(() =>
  import("@/features/chatbots/pages/DataCollection")
);

const SetupIntro = lazy(() =>
  import("@/features/chatbots/pages/SetupIntro")
);


const Deploy = lazy(() =>
  import("@/features/ai-chatbot-v1/pages/Deploy")
);

const AiChatbotAccessGate = lazy(() =>
  import("@/features/chatbots/components/AiChatbotAccessGate")
);
const AppointmentAccessGate = lazy(() =>
  import("@/features/appointment/components/AppointmentAccessGate")
);

const WhatsAppIntegration = lazy(() =>
  import("@/features/whats-app-crm/pages/WhatsAppIntegration")
);

const WhatsAppTemplates = lazy(() =>
  import("@/features/whats-app-crm/pages/WhatsAppTemplates")
);

const WhatsAppContacts = lazy(() =>
  import("@/features/whats-app-crm/pages/WhatsAppContacts")
);

const WhatsAppAttributes = lazy(() =>
  import("@/features/whats-app-crm/pages/WhatsAppAttributes")
);

const WhatsAppCampaigns = lazy(() =>
  import("@/features/whats-app-crm/pages/WhatsAppCampaigns")
);
const WhatsAppDripCampaigns = lazy(() =>
  import("@/features/whats-app-crm/pages/WhatsAppDripCampaigns")
);

const OptOutSettings = lazy(() =>
  import("@/features/whats-app-crm/pages/OptOutSettings")
);


const ApiKeys = lazy(() =>
  import("@/features/whats-app-crm/pages/ApiKeys")
);
const ApiIntegrationAccessGate = lazy(() =>
  import("@/features/whats-app-crm/components/ApiIntegrationAccessGate")
);

const WorkflowsList = lazy(() =>
  import("@/features/workflow/pages/WorkflowsList")
);

const WorkflowLibrary = lazy(() =>
  import("@/features/workflow/pages/WorkflowLibrary")
);

const Workflow = lazy(() =>
  import("@/features/workflow/pages/Workflow")
);

const ApiManagement = lazy(() =>
  import("@/features/workflow/pages/ApiManagement")
);

const FormBuilder = lazy(() =>
  import("@/features/workflow/pages/FormBuilder")
);

const FlowResponses = lazy(() =>
  import("@/features/workflow/pages/FlowResponses")
);

const WorkflowAccessGate = lazy(() =>
  import("@/features/workflow/components/WorkflowAccessGate")
);


const GlobalLiveChatPage = lazy(() =>
  import("@/features/live-chat/pages/GlobalLiveChatPage").then(module => ({
    default: module.GlobalLiveChatPage,
  }))
);




const CreateCampaignPage = lazy(() =>
  import("@/features/whats-app-crm/pages/CreateCampaignPage")
);

const CampaignDetailsPage = lazy(() =>
  import("@/features/whats-app-crm/pages/CampaignDetailsPage")
);
const CreateDripCampaignPage = lazy(() =>
  import("@/features/whats-app-crm/pages/CreateDripCampaignPage")
);
const DripCampaignDetailsPage = lazy(() =>
  import("@/features/whats-app-crm/pages/DripCampaignDetailsPage")
);

const CreateContactPage = lazy(() =>
  import("@/features/whats-app-crm/pages/CreateContactPage")
);

const PaymentHistory = lazy(() =>
  import("@/features/plan/pages/PaymentHistory")
);

const FreeTools = lazy(() =>
  import("@/features/free-tools/pages/FreeTools")
);

// Real Estate Pages
const PropertyList = lazy(() =>
  import("@/features/real-estate/pages/PropertyList")
);
const PropertyForm = lazy(() =>
  import("@/features/real-estate/pages/PropertyForm")
);
const PropertyLeads = lazy(() =>
  import("@/features/real-estate/pages/PropertyLeads")
);
const RealEstateSetupIntro = lazy(() =>
  import("@/features/real-estate/pages/RealEstateSetupIntro")
);
const RealEstateSetupForm = lazy(() =>
  import("@/features/real-estate/pages/RealEstateSetupForm")
);



export const routes: RouteObject[] = [
  // Auth Routes
  {
    path: "/login",
    element: <LoginPage />,
  },
  {
    path: "/remote-login",
    element: <RemoteLoginPage />,
  },
  {
    path: "/register",
    element: <RegisterPage />,
  },
  {
    path: "/verify-otp",
    element: <VerifyOtpPage />,
  },
  {
    path: "/forgot-password",
    element: <ForgotPasswordPage />,
  },
  {
    path: "/confirm-password/:token",
    element: <ResetPasswordPage />,
  },
  {
    path: "/",
    element: <LoginPage />,
  },
  {
    path: "/onboarding",
    element: (
      <ProtectedRoute>
        <OnboardingPage />
      </ProtectedRoute>
    ),
  },


  {
    path: "/analytics",
    element: (
      <ProtectedRoute>
        <Analytics />
      </ProtectedRoute>
    ),
  },

  // Chatbot Builder with Nested Routes (Persistent Sidebar)
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <ChatbotBuilderLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: "",
        element: <Navigate to="/dashboard" replace />,
      },
      // AI Chatbot Steps
      {
        path: "dashboard",
        element: <WhatsAppIntegration />,
      },
      {
        path: "plans",
        element: <PermissionRoute permission="subscription.view"><Plans /></PermissionRoute>,
      },
      {
        path: "basic-info",
        element: <PermissionRoute permission="chatbots.view"><BasicInfo /></PermissionRoute>,
      },

      {
        path: "data-collection",
        element: <PermissionRoute permission="chatbots.view"><DataCollection /></PermissionRoute>,
      },

      {
        path: "deploy",
        element: <PermissionRoute permission="chatbots.view"><Deploy /></PermissionRoute>,
      },
      {
        path: "intro",
        element: <PermissionRoute permission="chatbots.view"><SetupIntro /></PermissionRoute>,
      },
      {
        path: "onboarding",
        children: [
          {
            path: "basic-info",
            element: <PermissionRoute permission="chatbots.view"><BasicInfo /></PermissionRoute>,
          },
          {
            path: "data-collection",
            element: <PermissionRoute permission="chatbots.view"><DataCollection /></PermissionRoute>,
          },
          {
            path: "deploy",
            element: <PermissionRoute permission="chatbots.view"><Deploy /></PermissionRoute>,
          },
        ]
      },

      // WhatsApp Routes
      {
        path: "whatsapp",
        children: [
          {
            path: "",
            element: <Navigate to="/dashboard" replace />,
          },
          {
            path: "templates",
            children: [
              {
                path: "",
                element: <PermissionRoute permission="whatsapp_templates.view"><WhatsAppTemplates /></PermissionRoute>,
              },
              {
                path: "create",
                element: <PermissionRoute permission={["whatsapp_templates.create", "whatsapp_templates.update"]}><WhatsAppTemplateBuilder /></PermissionRoute>,
              },
              {
                path: "edit/:templateId",
                element: <PermissionRoute permission={["whatsapp_templates.create", "whatsapp_templates.update"]}><WhatsAppTemplateBuilder /></PermissionRoute>,
              },
              {
                path: "view/:templateId",
                element: <PermissionRoute permission={["whatsapp_templates.view"]}><ViewTemplatePage /></PermissionRoute>,
              },
              {
                path: "samples",
                element: <PermissionRoute permission={["whatsapp_templates.view"]}><TemplateLibrary /></PermissionRoute>,
              },
            ],
          },

          {
            path: "opt-inout-settings",
            element: <OwnerRoute><OptOutSettings /></OwnerRoute>,
          },
          {
            path: "api-keys",
            element: <PermissionRoute permission="api_integration_key.view"><ApiKeys /></PermissionRoute>,
          },
          {
            path: "campaigns",
            children: [
              {
                path: "",
                element: <PermissionRoute permission="whatsapp_campaign.view"><WhatsAppCampaigns /></PermissionRoute>,
              },
              {
                path: "create",
                element: <PermissionRoute permission="whatsapp_campaign.create"><CreateCampaignPage /></PermissionRoute>,
              },
              {
                path: "edit/:campaignId",
                element: <PermissionRoute permission="whatsapp_campaign.update"><CreateCampaignPage /></PermissionRoute>,
              },
              {
                path: ":campaignId",
                element: <PermissionRoute permission="whatsapp_campaign.view"><CampaignDetailsPage /></PermissionRoute>,
              },
            ],
          },
          {
            path: "api-integration",
            element: <PermissionRoute permission="api_integration_key.view"><ApiIntegrationAccessGate /></PermissionRoute>,
          },
        ]
      },
      {
        path: "automations",
        children: [
          {
            path: "attributes",
            element: <PermissionRoute permission="attribute.view"><WhatsAppAttributes /></PermissionRoute>,
          },
          {
            path: "drip-campaigns",
            element: <WorkflowAccessGate />,
            children: [
              {
                path: "",
                element: <PermissionRoute permission="whatsapp_drip_campaign.view"><WhatsAppDripCampaigns /></PermissionRoute>,
              },
              {
                path: "create",
                element: <PermissionRoute permission="whatsapp_drip_campaign.create"><CreateDripCampaignPage /></PermissionRoute>,
              },
              {
                path: "edit/:dripCampaignId",
                element: <PermissionRoute permission="whatsapp_drip_campaign.update"><CreateDripCampaignPage /></PermissionRoute>,
              },
              {
                path: ":dripCampaignId",
                element: <PermissionRoute permission="whatsapp_drip_campaign.view"><DripCampaignDetailsPage /></PermissionRoute>,
              },
            ],
          },
          {
            element: <WorkflowAccessGate />,
            children: [
              {
                path: "workflows",
                element: <PermissionRoute permission="workflow.view"><WorkflowsList /></PermissionRoute>,
              },
              {
                path: "workflows/:workflowId",
                element: <PermissionRoute permission="workflow.view"><Workflow /></PermissionRoute>,
              },
              {
                path: "library",
                element: <PermissionRoute permission="workflow.view"><WorkflowLibrary /></PermissionRoute>,
              },
              {
                path: "api-management",
                element: <PermissionRoute permission="workflow_api_management.view"><ApiManagement /></PermissionRoute>,
              },
              {
                path: "flow-builder",
                element: <PermissionRoute permission={["flow_builder.view", "flow_builder.create", "flow_builder.update"]}><FormBuilder /></PermissionRoute>,
              },
              {
                path: "flow-builder/:flowId/responses",
                element: (
                  <PermissionRoute permission={["flow_builder.view", "flow_builder.create", "flow_builder.update"]}>
                    <FlowResponses />
                  </PermissionRoute>
                ),
              },
            ]
          },
        ]
      },
      {
        path: "contacts",
        children: [
          {
            path: "",
            element: <PermissionRoute permission="contacts.view"><WhatsAppContacts /></PermissionRoute>,
          },
          {
            path: "create",
            element: <PermissionRoute permission="contacts.create"><CreateContactPage /></PermissionRoute>,
          },
          {
            path: "edit",
            element: <PermissionRoute permission="contacts.update"><CreateContactPage /></PermissionRoute>,
          },
        ],
      },

      // Free Tools Route
      {
        path: "free-tools",
        element: <FreeTools />,
      },
      // WhatsApp Link Generator Route
      {
        path: "whatsapp-link-generator",
        element: (
          <Suspense fallback={<div>Loading...</div>}>
            <WhatsAppLinkGenerator />
          </Suspense>
        ),
      },
      // WhatsApp QR Generator Route
      {
        path: "whatsapp-qr-generator",
        element: (
          <Suspense fallback={<div>Loading...</div>}>
            <WhatsAppQRGenerator />
          </Suspense>
        ),
      },
      // WhatsApp Widget Generator Route
      {
        path: "whatsapp-widget-generator",
        element: (
          <Suspense fallback={<div>Loading...</div>}>
            <WhatsAppWidgetGenerator />
          </Suspense>
        ),
      },
      // Live Chat Route
      {
        path: "live-chat",
        element: <PermissionRoute permission="live_chat.manage"><GlobalLiveChatPage /></PermissionRoute>,
      },
      {
        path: "instagram",
        children: [
          {
            path: "connection",
            element: <PermissionRoute permission="instagram_automation.view"><InstagramConnectionPage /></PermissionRoute>,
          },
          {
            path: "automation",
            element: <PermissionRoute permission="instagram_automation.view"><InstagramAutomationListPage /></PermissionRoute>,
          },
          {
            path: "automation/create",
            element: <PermissionRoute permission="instagram_automation.create"><InstagramAutomationBuilderPage /></PermissionRoute>,
          },
          {
            path: "automation/:ruleId/edit",
            element: <PermissionRoute permission="instagram_automation.update"><InstagramAutomationBuilderPage /></PermissionRoute>,
          },
          {
            path: "automation/:ruleId",
            element: <PermissionRoute permission="instagram_automation.view"><InstagramAutomationDetailPage /></PermissionRoute>,
          },
        ],
      },
      {
        path: "appointment",
        element: <PermissionRoute permission="appointment.view"><AppointmentAccessGate /></PermissionRoute>,
        children: [
          {
            path: "",
            element: <Appointment />,
          },
          {
            path: "users/:appointmentUserId",
            element: <Appointment />,
          },
          {
            path: "users/:appointmentUserId/:userModule",
            element: <Appointment />,
          },
          {
            path: ":module",
            element: <Appointment />,
          },
        ],
      },
      // AI Chatbot v1 Routes
      {
        path: "ai-chatbot",
        element: <AiChatbotAccessGate />,
        children: [
          {
            path: "knowledge-sources",
            children: [
              {
                path: "",
                element: <PermissionRoute permission="ai_chatbot.view"><KnowledgeSources /></PermissionRoute>,
              },
              {
                path: ":sourceId",
                element: <PermissionRoute permission="ai_chatbot.view"><KnowledgeSourceDetail /></PermissionRoute>,
              },
            ],
          },
          {
            path: "settings",
            element: <PermissionRoute permission="ai_chatbot.view"><AiChatbotV1Settings /></PermissionRoute>,
          },
          {
            path: "api-tools",
            children: [
              {
                path: "",
                element: <PermissionRoute permission="ai_chatbot.view" superAdminOrRemoteOnly><AiChatbotV1ApiTools /></PermissionRoute>,
              },
              {
                path: ":toolId",
                element: <PermissionRoute permission="ai_chatbot.view" superAdminOrRemoteOnly><AiChatbotV1ApiToolEditor /></PermissionRoute>,
              },
            ],
          },
          {
            path: "leads",
            element: <PermissionRoute permission="ai_chatbot.view"><AiChatbotV1Leads /></PermissionRoute>,
          },
          {
            path: "test-chat",
            element: <PermissionRoute permission="ai_chatbot.view"><AiChatbotV1TestChat /></PermissionRoute>,
          },
          {
            path: "user-dashboard",
            element: <PermissionRoute permission="ai_chatbot.view"><AiChatbotV1UserDashboard /></PermissionRoute>,
          },
          {
            path: "usage-logs",
            element: <PermissionRoute permission="ai_chatbot.view"><AiChatbotV1UsageLogs /></PermissionRoute>,
          },
          {
            path: "usage",
            children: [
              {
                path: "",
                element: <PermissionRoute permission="ai_chatbot.view" superAdminOrRemoteOnly><AiChatbotV1Usage /></PermissionRoute>,
              },
              {
                path: "sources/:sourceId/pages",
                element: <PermissionRoute permission="ai_chatbot.view" superAdminOrRemoteOnly><AiChatbotV1UsageSourcePageDetail /></PermissionRoute>,
              },
              {
                path: "sources/:sourceId",
                element: <PermissionRoute permission="ai_chatbot.view" superAdminOrRemoteOnly><AiChatbotV1UsageSourceDetail /></PermissionRoute>,
              },
              {
                path: "conversations/:conversationId",
                element: <PermissionRoute permission="ai_chatbot.view" superAdminOrRemoteOnly><AiChatbotV1UsageConversationDetail /></PermissionRoute>,
              },
              {
                path: "conversations/:conversationId/messages/:messageId",
                element: <PermissionRoute permission="ai_chatbot.view" superAdminOrRemoteOnly><AiChatbotV1UsageMessageEvents /></PermissionRoute>,
              },
            ],
          },
        ]
      },
      // Dashboard content inside builder
      {
        path: "analytics",
        children: [
          {
            path: "",
            element: <PermissionRoute permission="analytics.view"><Analytics /></PermissionRoute>,
          },
          {
            path: "leads",
            element: <PermissionRoute permission="analytics.view"><AttributesReportsPage /></PermissionRoute>,
          },
          {
            path: "flow-reports",
            element: <PermissionRoute permission="analytics.view"><FlowReports /></PermissionRoute>,
          },
        ],
      },
      {
        path: "plans",
        element: <PermissionRoute permission="subscription.view"><Plans /></PermissionRoute>,
      },
      {
        path: "settings",
        element: <PermissionRoute permission="settings.view"><Setting /></PermissionRoute>,
      },
      {
        path: "admin-settings",
        element: <PermissionRoute permission="settings.view"><AdminSettingsPage /></PermissionRoute>,
      },
      {
        element: <MultiAgentAccessGate />,
        children: [
          {
            path: "team",
            element: <PermissionRoute permission="team_management.view"><TeamManagement /></PermissionRoute>,
          },
          {
            path: "roles",
            element: <PermissionRoute permission="team_management.view"><RolesAndPermissions /></PermissionRoute>,
          },
          {
            path: "roles/create",
            element: <PermissionRoute permission={["team_management.create_role", "team_management.assign_permissions"]}><CreateRolePage /></PermissionRoute>,
          },
          {
            path: "roles/edit/:roleId",
            element: <PermissionRoute permission={["team_management.update_role", "team_management.assign_permissions"]}><EditRolePage /></PermissionRoute>,
          },
        ],
      },
      {
        path: "payment-history",
        element: <PermissionRoute permission="subscription.view"><PaymentHistory /></PermissionRoute>,
      },
      {
        path: "affiliate",
        element: (
          <ProtectedRoute>
            <AffiliateDashboard />
          </ProtectedRoute>
        ),
      },
      {
        path: "real-estate",
        children: [
          {
            path: "",
            element: <PermissionRoute permission="real_estate.view"><PropertyList /></PermissionRoute>,
          },
          {
            path: "setup-intro",
            element: <PermissionRoute permission="real_estate.view"><RealEstateSetupIntro /></PermissionRoute>,
          },
          {
            path: "setup",
            element: <PermissionRoute permission="real_estate.update"><RealEstateSetupForm /></PermissionRoute>,
          },
          {
            path: "create",
            element: <PermissionRoute permission="real_estate.create"><PropertyForm /></PermissionRoute>,
          },
          {
            path: "edit/:propertyId",
            element: <PermissionRoute permission="real_estate.update"><PropertyForm /></PermissionRoute>,
          },
          {
            path: "leads",
            element: <PermissionRoute permission="real_estate.view_leads"><PropertyLeads /></PermissionRoute>,
          },
        ],
      },
    ]
  },

  {
    path: "/chatbot-builder/:id/*",
    element: <LegacyRedirect />,
  },
  {
    path: "/whatsapp/dashboard",
    element: <Navigate to="/dashboard" replace />,
  },
  {
    path: "/whatsapp/live-chat",
    element: <Navigate to="/live-chat" replace />,
  },
  {
    path: "/whatsapp/contacts",
    element: <Navigate to="/contacts" replace />,
  },
  {
    path: "/whatsapp/appointment",
    element: <Navigate to="/appointment" replace />,
  },
  {
    path: "/whatsapp/plans",
    element: <Navigate to="/plans" replace />,
  },
  {
    path: "/whatsapp/payment-history",
    element: <Navigate to="/payment-history" replace />,
  },
  {
    path: "/whatsapp/affiliate",
    element: <Navigate to="/affiliate" replace />,
  },
  {
    path: "/templates/*",
    element: <FlatToWhatsAppRedirect />,
  },
  {
    path: "/attributes/*",
    element: <FlatToAutomationsRedirect />,
  },
  {
    path: "/whatsapp/attributes/*",
    element: <FlatToAutomationsRedirect />,
  },
  {
    path: "/flow-builder/*",
    element: <FlatToAutomationsRedirect />,
  },
  {
    path: "/api-management/*",
    element: <FlatToAutomationsRedirect />,
  },
  {
    path: "/library/*",
    element: <FlatToAutomationsRedirect />,
  },
  {
    path: "/opt-inout-settings/*",
    element: <FlatToWhatsAppRedirect />,
  },
  {
    path: "/api-keys/*",
    element: <FlatToWhatsAppRedirect />,
  },
  {
    path: "/campaigns/*",
    element: <FlatToWhatsAppRedirect />,
  },
  {
    path: "/drip-campaigns/*",
    element: <FlatToAutomationsRedirect />,
  },
  {
    path: "/whatsapp/drip-campaigns/*",
    element: <FlatToAutomationsRedirect />,
  },
  {
    path: "/workflows/*",
    element: <FlatToAutomationsRedirect />,
  },
  {
    path: "/whatsapp/workflows/*",
    element: <FlatToAutomationsRedirect />,
  },
  {
    path: "/api-integration/*",
    element: <FlatToWhatsAppRedirect />,
  },

  {
    path: "settings",
    element: (
      <ProtectedRoute>
        <Setting />
      </ProtectedRoute>
    ),
  },

  {
    path: "*",
    element: <NotFound />,
  },
];

